import { CheckCircle2, Clock, ChevronDown, Loader2 } from "lucide-react";
import { Avatar, Badge, Button, Dropdown, Empty, Space, Table, Tooltip, notification } from "antd";
import { useEffect, useState, useRef } from "react";
import GitCommitLink from "../../components/GitCommitLink";
import moment from "moment";
import { getEnvironmentService, getPipelinesService } from "../../backend";
import { useNavigate, useParams } from "react-router";
import BuildStatusTag from "../../components/BuildStatusTag";
import { Component, IEnvDetails, PipelineItem, PipelineItemTarget } from "../../types";
import BuildDetailsDrawer from "../../components/BuildDetailsDrawer";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import { newUpdateNotificationsState } from "../../recoil/notification";
import { useRecoilValue } from "recoil";
import ComponentDeployDetailsDrawer from "../../components/ComponentDeployDetailsDrawer";
import AppDeployDetailsDrawer from "../../components/AppDeployDetailsDrawer";
import { InlineObject1TypesEnum } from "@microtica/ms-ap-sdk";
import SafeAvatar from "../../components/SafeAvatar";

interface AppPipelinesProps {
    env: IEnvDetails;
    component: Component;
}

export default function AppPipelines({
    env,
    component
}: AppPipelinesProps) {
    const navigate = useNavigate();
    const updates = useRecoilValue(newUpdateNotificationsState);
    const [branches, setBranches] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [pipelineId, setPipelineId] = useState<string>();
    const [pipelines, setPipelines] = useState<PipelineItem[]>([]);
    const { projectId, envId, componentId, appName } = useParams() as { projectId: string; envId: string; componentId: string; appName: string };
    const [showPipelineDetails, setShowPipelineDetails] = useState(false);
    const [selectedPipeline, setSelectedPipeline] = useState<PipelineItem>();
    const [activeDeployment, setActiveDeployment] = useState<string>();
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 10;
    const loadingRef = useRef(false); // Prevent concurrent loadData calls
    const componentPipelineIdRef = useRef<string>(); // Cache component pipelineId to avoid duplicate getComponent calls
    const isComponentPublicRef = useRef<boolean>(); // Cache isComponentPublic flag
    const pipelineInfoRef = useRef<{ gitAccountId?: string; repositoryUrl?: string }>(); // Cache pipeline info for branch loading

    const disableManualBuild = false// !!buildMinutesUsedPercentage && buildMinutesUsedPercentage >= 100;

    useEffect(() => {
        setLoading(true);
        setPipelines([]);
        setBranches([]); // Reset branches when component/app changes
        componentPipelineIdRef.current = undefined; // Reset cache when component changes
        pipelineInfoRef.current = undefined; // Reset pipeline info cache
        isComponentPublicRef.current = undefined; // Reset isComponentPublic cache
        setCurrentPage(1); // Reset to first page when component/app changes
        loadData(1);
    }, [componentId, appName]);


    useEffect(() => {
        if (updates.some(update => update.projectId === projectId)) {
            setLoading(true);
            loadData(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updates]);

    useEffect(() => {
        setLoading(true);
        loadData(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const getCurrentDeployment = async () => {
        if (appName) {
            const app = component.apps?.find(a => a.name === appName)!;
            return {
                isComponentPublic: false,
                version: app.deployment?.version,
                pipelineId: app.deployment?.pipelineId!
            };
        } else {
            // Use cached pipelineId if available to avoid duplicate getComponent calls
            if (componentPipelineIdRef.current) {
                return {
                    isComponentPublic: component.isPublic,
                    version: component.version,
                    pipelineId: componentPipelineIdRef.current
                };
            }

            const { data: { pipelineId } } = await getEnvironmentService().getComponent(component.component!.id, projectId);
            componentPipelineIdRef.current = pipelineId; // Cache the pipelineId

            return {
                isComponentPublic: component.isPublic,
                version: component.version,
                pipelineId
            }
        }
    }

    const loadData = async (page: number = currentPage) => {
        // Prevent concurrent calls
        if (loadingRef.current) {
            return;
        }

        try {
            loadingRef.current = true;
            setLoading(true);
            const offset = (page - 1) * pageSize;

            // Start both API calls in parallel for optimal performance
            const [deploymentInfo, pipelinesResponse] = await Promise.all([
                getCurrentDeployment(),
                getPipelinesService().getCombinedPipelines(
                    projectId,
                    {
                        envIds: envId ? [envId] : undefined,
                        resourceName: appName ? undefined : componentId,
                        appName,
                        types: [InlineObject1TypesEnum.Deploy], // Only fetch builds and buildonly pipelines
                        limit: pageSize,
                        kubernetesId: appName ? component.clusterId : undefined,
                        offset
                    }
                ).then(response => ({
                    data: {
                        pipelines: response.data.pipelines.map((p: any) => ({
                            ...p,
                            startDate: new Date(p.startDate),
                            stopDate: p.stopDate ? new Date(p.stopDate) : undefined
                        })),
                        total: response.data.total
                    }
                }))
            ]);

            const { pipelineId, version, isComponentPublic } = deploymentInfo;
            const { data: { pipelines: allPipelines, total: totalCount } } = pipelinesResponse;

            // Cache flags for branch loading (pipeline details will be fetched when needed)
            isComponentPublicRef.current = isComponentPublic;

            // Filter by component/app name (type filtering is now done on backend)
            const filteredPipelines = allPipelines.filter(p => {
                if (appName) {
                    return p.targets.some((t: PipelineItemTarget) => t.name === appName)
                } else {
                    return p.targets.some((t: PipelineItemTarget) => t.name === componentId)
                }
            });
            const currentDeployment = filteredPipelines.find(p => p.targets.some((t: PipelineItemTarget) => t.commit.version === version));

            setPipelines(allPipelines);
            setTotal(totalCount);
            setPipelineId(pipelineId);
            setActiveDeployment(currentDeployment?.id);
        } catch (error) {
            console.error("Failed to load pipelines:", error);
            // Keep pipelines as is, but ensure loading is set to false
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }

    const loadBranches = async () => {
        // Check if component is public - no branches for public components
        if (isComponentPublicRef.current) {
            setBranches([]);
            return;
        }

        // If pipeline info is already cached, use it
        if (pipelineInfoRef.current?.gitAccountId && pipelineInfoRef.current?.repositoryUrl) {
            try {
                setLoadingBranches(true);
                const { data: { branches } } = await getPipelinesService().listGitAccountRepositoryBranches(
                    projectId,
                    pipelineInfoRef.current.gitAccountId,
                    pipelineInfoRef.current.repositoryUrl
                );
                setBranches(branches);
            } catch (error) {
                console.error("Failed to load branches:", error);
                setBranches([]);
            } finally {
                setLoadingBranches(false);
            }
            return;
        }

        // Need to fetch pipeline details first
        if (!pipelineId) {
            setBranches([]);
            return;
        }

        try {
            setLoadingBranches(true);
            // Fetch pipeline details to get gitAccountId and repositoryUrl
            const { data: pipeline } = await getPipelinesService().getPipelineById(projectId, pipelineId);

            // Cache pipeline info for future use
            pipelineInfoRef.current = {
                gitAccountId: pipeline.gitAccountId,
                repositoryUrl: pipeline.repositoryUrl
            };

            if (!pipeline.gitAccountId || !pipeline.repositoryUrl) {
                setBranches([]);
                return;
            }

            // Now fetch branches
            const { data: { branches } } = await getPipelinesService().listGitAccountRepositoryBranches(
                projectId,
                pipeline.gitAccountId,
                pipeline.repositoryUrl
            );
            setBranches(branches);
        } catch (error) {
            console.error("Failed to load branches:", error);
            setBranches([]);
        } finally {
            setLoadingBranches(false);
        }
    }

    const handlePipelineTrigger = async (branch: string) => {
        try {
            await getPipelinesService()
                .triggerPipeline(projectId, pipelineId!, { ref: `refs/heads/${branch}` });

            notification.success({
                message: "Build in Progress",
                description: <>
                    It could take a few moments for the changes to take effect.
                    <div>
                        <a href={`/projects/${projectId}/pipelines`}>View in Pipelines</a>
                    </div>
                </>,
                icon: <Loader2 size={16} color="var(--primary-color)" className="animate-spin" />
            });
        } catch (error) {
            notification.error({
                message: "Error triggering build",
                description: (error as any).response?.data?.message || "We encountered an error while trying to trigger a build. Please try again."
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Space direction="vertical" size="large" className="flex-justify-space-between" style={{ width: "100%" }}>
            <div className="flex-justify-space-between flex-align-center">
                <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                        Deployment Pipelines
                    </div>
                    <div className="gray-text" style={{ fontWeight: 400, fontSize: 14 }}>
                        Only pipelines that triggered deployments on this specific component are displayed here.
                    </div>
                </div>
                <Dropdown
                    trigger={["click"]}
                    placement="bottomRight"
                    onOpenChange={(open) => {
                        if (open && branches.length === 0 && !loadingBranches) {
                            loadBranches();
                        }
                    }}
                    menu={{
                        direction: "ltr",
                        items: loadingBranches ? [
                            {
                                key: "loading",
                                label: <span><Loader2 size={14} className="animate-spin" /> Loading branches...</span>,
                                disabled: true
                            }
                        ] : branches.length === 0 ? [
                            {
                                key: "no-branches",
                                label: "No branches available",
                                disabled: true
                            }
                        ] : [
                            ...branches.map(branch => ({
                                key: branch,
                                label: branch,
                                onClick: () => handlePipelineTrigger(branch)
                            }))
                        ]
                    }}
                >
                    <Tooltip
                        title={
                            disableManualBuild ?
                                "No more available build minutes in this plan" :
                                undefined
                        }
                    >
                        <Button
                            disabled={disableManualBuild}
                        >
                            Trigger from branch <ChevronDown size={14} />
                        </Button>
                    </Tooltip>
                </Dropdown>
            </div>
            <div style={{ width: "100%", minWidth: 0 }}>
                <Table
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} pipelines`,
                        onChange: (page) => {
                            setCurrentPage(page);
                        }
                    }}
                    className="ant-table-selectable"
                    loading={loading}
                    dataSource={pipelines}
                    scroll={{ y: "calc(100vh - 520px)", x: "max-content" }}
                    locale={{
                        emptyText:
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <>
                                        <div>
                                            This {appName ? "app" : "component"} does not have any pipelines yet!
                                        </div>
                                        <div>
                                            Please either push changes on Git or manually trigger a pipeline from the branch.
                                        </div>
                                    </>
                                }
                            />
                    }}
                    columns={[
                        {
                            title: 'Commit',
                            key: 'commit',
                            render: (item: PipelineItem) => {
                                const target = item.targets
                                    .filter(t => t.name === appName || t.name === componentId)
                                    .find(() => true);

                                if (!target) {
                                    return <span style={{ color: "#9ca3af", fontSize: "13px" }}>No commit</span>;
                                }

                                const isActive = item.id === activeDeployment;

                                return (
                                    <div style={{ fontSize: "13px", lineHeight: "1.5" }}>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "10px"
                                        }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontWeight: 500,
                                                    marginBottom: "6px",
                                                    color: "#111827",
                                                    lineHeight: 1.4
                                                }}>
                                                    {target.commit.message}
                                                </div>
                                                <div style={{ color: "#6b7280", fontSize: "12px" }}>
                                                    <GitCommitLink
                                                        version={target.commit.sha || target.commit.version}
                                                        repoUrl={target.repository}
                                                        branch={target.commit.name}
                                                    />
                                                </div>
                                            </div>
                                            {isActive && (
                                                <Tooltip title="Currently deployed">
                                                    <CheckCircle2 size={20} color="#10b981" style={{ flexShrink: 0, marginTop: "2px" }} />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'time',
                            title: "Started",
                            render: (item: PipelineItem) => (
                                <div style={{ fontSize: "13px", lineHeight: "1.5" }}>
                                    <div style={{ marginBottom: "8px", color: "#111827" }}>
                                        {moment(item.startDate).fromNow()}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#6b7280", fontSize: "12px" }}>
                                        <SafeAvatar className="no-border" src={item.initiatorAvatar} size={18} />
                                        <span>{item.initiatorName}</span>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: 'status',
                            title: "Status",
                            align: "right",
                            render: (build: PipelineItem) => (
                                build.stage === "deploy" ?
                                    <DeploymentStatusTag status={build.status} /> :
                                    <BuildStatusTag status={build.status} statusCode={build.statusCode} />
                            )
                        }
                    ]}
                    onRow={build => {
                        return {
                            onClick: () => {
                                setSelectedPipeline(build)
                                setShowPipelineDetails(true);
                            }, // click row
                        };
                    }}
                />
            </div>
            {
                showPipelineDetails ? (
                    <>
                        {
                            selectedPipeline?.build &&
                            <BuildDetailsDrawer
                                type={selectedPipeline.type}
                                projectId={projectId}
                                pipelineId={selectedPipeline.build?.pipelineId!}
                                buildId={selectedPipeline.build?.id!}
                                open={showPipelineDetails}
                                onClose={() => setShowPipelineDetails(false)}
                            />
                        }
                        {
                            selectedPipeline?.targetType === "environment" &&
                            <ComponentDeployDetailsDrawer
                                type={selectedPipeline.type}
                                projectId={projectId}
                                envId={selectedPipeline.envId!}
                                cloudProvider={env.cloudProvider!}
                                iac={env.infrastructureAsCodeTool!}
                                deploymentId={selectedPipeline.id}
                                open={showPipelineDetails}
                                onClose={() => setShowPipelineDetails(false)}
                            />
                        }
                        {
                            selectedPipeline?.targetType === "app" &&
                            <AppDeployDetailsDrawer
                                type={selectedPipeline.type}
                                projectId={projectId}
                                envId={selectedPipeline.envId!}
                                deploymentId={selectedPipeline.id}
                                open={showPipelineDetails}
                                onClose={() => setShowPipelineDetails(false)}
                            />
                        }
                    </>
                ) : null
            }
        </Space>
    )
}